import * as React from 'react';
import { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from './button';
import { Input } from './input';
import { cn } from '../../lib/utils';

/**
 * Field configuration for InlineForm inputs.
 */
export interface InlineFormField {
  readonly name: string;
  readonly label: string;
  readonly placeholder?: string;
  readonly required?: boolean;
  readonly type?: 'text' | 'email';
}

/**
 * Props for the InlineForm component.
 * Renders a compact inline form for entity creation without page navigation.
 */
export interface InlineFormProps {
  readonly onSubmit: (data: Record<string, string>) => Promise<void>;
  readonly onCancel: () => void;
  readonly fields: readonly InlineFormField[];
  readonly submitLabel: string;
}

/**
 * InlineForm component for quick inline entity creation.
 *
 * Features:
 * - Auto-focuses first input on mount
 * - Escape key closes form and discards input
 * - Disables inputs and shows loading state during submission
 * - Preserves entered values and shows error alert on failure
 * - Accessible: labels, aria-invalid, aria-describedby for error linking
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 12.5
 */
export function InlineForm({ fields, onSubmit, onCancel, submitLabel }: InlineFormProps) {
  const firstInputRef = useRef<HTMLInputElement>(null);
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of fields) {
      initial[field.name] = '';
    }
    return initial;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Auto-focus first input on mount (Requirement 6.2)
  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  // Handle Escape key to close form and discard input (Requirement 6.3)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [onCancel],
  );

  const handleChange = useCallback((fieldName: string, value: string) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }));
    // Clear field error when user starts typing
    setFieldErrors((prev) => {
      if (prev[fieldName]) {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      }
      return prev;
    });
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Client-side validation for required fields
      const newFieldErrors: Record<string, string> = {};
      for (const field of fields) {
        if (field.required && !values[field.name]?.trim()) {
          newFieldErrors[field.name] = `${field.label} is required`;
        }
      }

      if (Object.keys(newFieldErrors).length > 0) {
        setFieldErrors(newFieldErrors);
        return;
      }

      // Requirement 6.4: disable inputs, show loading, call onSubmit
      setIsSubmitting(true);
      setError(null);

      try {
        await onSubmit(values);
      } catch (err) {
        // Requirement 6.5: re-enable inputs, preserve values, show error
        const message = err instanceof Error ? err.message : 'An error occurred. Please try again.';
        setError(message);
        setIsSubmitting(false);
      }
    },
    [fields, values, onSubmit],
  );

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
      className={cn('flex flex-col gap-3 rounded-md border border-input bg-background p-4')}
    >
      {fields.map((field, index) => {
        const fieldError = fieldErrors[field.name];
        const errorId = `${field.name}-error`;

        return (
          <div key={field.name} className="flex flex-col gap-1.5">
            <label
              htmlFor={field.name}
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {field.label}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <Input
              ref={index === 0 ? firstInputRef : undefined}
              id={field.name}
              name={field.name}
              type={field.type ?? 'text'}
              placeholder={field.placeholder}
              value={values[field.name] ?? ''}
              onChange={(e) => handleChange(field.name, e.target.value)}
              disabled={isSubmitting}
              aria-invalid={fieldError ? true : undefined}
              aria-describedby={fieldError ? errorId : undefined}
              aria-required={field.required || undefined}
            />
            {fieldError && (
              <p id={errorId} role="alert" className="text-sm text-destructive">
                {fieldError}
              </p>
            )}
          </div>
        );
      })}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" loading={isSubmitting}>
          {submitLabel}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
